import { apiRequest } from './request'
import type { 
  ApiResponse
} from './types'
import { useUserStore } from '@/stores/user'

// ============================================================================
// 认证相关类型定义
// ============================================================================

/**
 * 设备类型枚举
 */
export enum DeviceType {
  Web = 1,
  Mobile = 2,
  Desktop = 3
}

/**
 * 设备信息
 */
export interface DeviceInfo {
  deviceId: string
  deviceType: string
  deviceDescription: string
  lastLoginAt: string
  clientIp: string
  isCurrentDevice: boolean
}

/**
 * Token信息
 */
export interface TokenInfo {
  tokenId: number
  userId: number
  tokenType: string
  createdAt: string
  expiresAt: string
  clientIp: string
  userAgent: string
  deviceId: string
  deviceType: string
  deviceDescription: string
}

/**
 * 用户登录请求
 */
export interface LoginRequest {
  userName: string
  password: string
  deviceType: DeviceType
}

/**
 * 短信验证码登录请求
 */
export interface SmsLoginRequest {
  phoneNumber: string
  code: string
  deviceType: DeviceType
}

/**
 * 平台短信验证码登录请求
 */
export interface PlatformSmsLoginRequest {
  phoneNumber: string
  code: string
  deviceType: DeviceType
}

/**
 * 发送短信验证码请求
 */
export interface SendSmsCodeRequest {
  phoneNumber: string
}

/**
 * 登录结果
 */
export interface LoginResult {
  accessToken: string
  refreshToken?: string
  expiresAt: string
  userInfo: AuthUserInfo
  isTemporaryToken: boolean
}

/**
 * 认证用户信息
 */
export interface AuthUserInfo {
  userId: number
  userName: string
  name?: string
  phone?: string
  email?: string
  roles?: string[]
  permissions?: string[]
}

/**
 * JWT用户信息
 */
export interface JwtUserInfo {
  userId: number
  userName: string
  roles?: string[]
  permissions?: string[]
  expiresAt: string
}

// ============================================================================
// 认证API服务类
// ============================================================================

export class AuthApiService {
  private readonly basePath = '/api/auth'

  // 用户登录
  async login(request: LoginRequest): Promise<ApiResponse<LoginResult>> {
    return apiRequest.post(`${this.basePath}/login`, request)
  }

  // 平台登录 - 直接处理用户名密码登录，获取正式token
  async platformLogin(request: LoginRequest): Promise<ApiResponse<LoginResult>> {
    return apiRequest.post(`${this.basePath}/platform-login`, request)
  }

  // 短信验证码登录
  async loginWithSms(request: SmsLoginRequest): Promise<ApiResponse<LoginResult>> {
    return apiRequest.post(`${this.basePath}/login-with-sms`, request)
  }

  // 平台短信验证码登录
  async platformLoginWithSms(request: PlatformSmsLoginRequest): Promise<ApiResponse<LoginResult>> {
    return apiRequest.post(`${this.basePath}/platform-login-with-sms`, request)
  }

  // 发送短信验证码
  async sendSmsCode(request: SendSmsCodeRequest): Promise<ApiResponse<any>> {
    return apiRequest.post(`${this.basePath}/send-sms-code`, request)
  }

  // 用户登出
  async logout(): Promise<ApiResponse<boolean>> {
    return apiRequest.post(`${this.basePath}/logout`)
  }

  // 刷新访问令牌
  async refreshToken(refreshToken: string): Promise<ApiResponse<string>> {
    return apiRequest.post(`${this.basePath}/refresh`, { refreshToken })
  }

  // 修改密码
  async changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse<boolean>> {
    return apiRequest.post(`${this.basePath}/change-password`, { oldPassword, newPassword })
  }

  // 重置密码
  async resetPassword(newPassword: string, targetUserId?: number): Promise<ApiResponse<boolean>> {
    const request: any = {
      newPassword: newPassword
    }
    
    // 如果提供了目标用户ID，说明是管理员重置他人密码
    if (targetUserId && targetUserId > 0) {
      request.targetUserId = targetUserId
    }
    
    return apiRequest.post(`${this.basePath}/reset-password`, request)
  }

  // 验证访问令牌
  async validateToken(): Promise<ApiResponse<boolean>> {
    return apiRequest.post(`${this.basePath}/validate`)
  }

  // 获取用户信息
  getProfile() {
    return apiRequest.get(`${this.basePath}/profile`)
  }

  // 获取用户token列表
  async getUserTokens(): Promise<ApiResponse<TokenInfo[]>> {
    return apiRequest.get(`${this.basePath}/tokens`)
  }

  // 撤销所有token
  async revokeAllTokens(): Promise<ApiResponse<boolean>> {
    return apiRequest.post(`${this.basePath}/revoke-all`)
  }

  // 撤销设备token
  async revokeDeviceTokens(deviceId: string): Promise<ApiResponse<boolean>> {
    return apiRequest.post(`${this.basePath}/revoke-device`, { deviceId })
  }

  // 获取用户设备列表
  async getUserDevices(): Promise<ApiResponse<DeviceInfo[]>> {
    return apiRequest.get(`${this.basePath}/devices`)
  }

  // 刷新用户信息（包括角色和权限）
  async refreshUserInfo(): Promise<ApiResponse<JwtUserInfo>> {
    return apiRequest.get(`${this.basePath}/profile`)
  }
}

// 创建认证API服务实例
export const authApi = new AuthApiService()

// ============================================================================
// 认证状态管理
// ============================================================================

export class AuthStateManager {
  private static instance: AuthStateManager
  private currentUser: JwtUserInfo | null = null

  private constructor() {}

  static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager()
    }
    return AuthStateManager.instance
  }

  // 设置当前用户
  setCurrentUser(user: JwtUserInfo): void {
    this.currentUser = user
    localStorage.setItem('currentUser', JSON.stringify(user))
    
    // 同时更新 user store
    const userStore = useUserStore()
    userStore.setUser(user)
  }

  // 获取当前用户
  getCurrentUser(): JwtUserInfo | null {
    if (!this.currentUser) {
      const stored = localStorage.getItem('currentUser')
      if (stored) {
        this.currentUser = JSON.parse(stored)
      }
    }
    return this.currentUser
  }

  // 清除认证状态
  clearAuthState(): void {
    this.currentUser = null
    // 在开发环境中，跳过清除操作以保持localStorage数据
    if (import.meta.env.DEV) {
      console.log('开发环境：跳过清除认证状态，保持localStorage数据')
      return
    }
    localStorage.removeItem('currentUser')
    apiRequest.clearToken()
    
    // 同时清除 user store
    const userStore = useUserStore()
    userStore.clearUser()
  }

  // 检查是否已登录
  isLoggedIn(): boolean {
    return !!apiRequest.getToken() && !!this.getCurrentUser()
  }
}

// 创建认证状态管理器实例
export const authStateManager = AuthStateManager.getInstance()

// ============================================================================
// 认证工具函数
// ============================================================================

export const authUtils = {
  // 处理登录流程 - 平台项目专用
  async handleLogin(loginRequest: LoginRequest): Promise<LoginResult> {
    try {
      // 直接调用平台登录，获取正式token
      const platformLoginResponse = await authApi.platformLogin(loginRequest)
      if (!platformLoginResponse.success) {
        throw new Error(platformLoginResponse.message || '平台登录失败')
      }

      const platformLoginResult = platformLoginResponse.data
      
      // 设置正式token和refreshToken
      apiRequest.setToken(platformLoginResult.accessToken)
      if (platformLoginResult.refreshToken) {
        apiRequest.setRefreshToken(platformLoginResult.refreshToken)
      }
      
      // 设置当前用户信息
      if (platformLoginResult.userInfo) {
        authStateManager.setCurrentUser({
          userId: platformLoginResult.userInfo.userId,
          userName: platformLoginResult.userInfo.userName,
          roles: platformLoginResult.userInfo.roles,
          permissions: platformLoginResult.userInfo.permissions,
          expiresAt: platformLoginResult.expiresAt
        })
      }
      
      return platformLoginResult
    } catch (error) {
      throw error
    }
  },

  // 处理登出流程
  async handleLogout(): Promise<void> {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('登出时发生错误:', error)
    } finally {
      // 清除本地状态
      authStateManager.clearAuthState()
    }
  },

  // 刷新token
  async refreshTokenIfNeeded(): Promise<boolean> {
    try {
      const token = apiRequest.getToken()
      const refreshToken = apiRequest.getRefreshToken()
      
      if (!token || !refreshToken) {
        return false
      }

      // 检查token是否即将过期（提前5分钟刷新）
      const tokenPayload = this.parseJwtToken(token)
      if (tokenPayload && tokenPayload.exp) {
        const currentTime = Math.floor(Date.now() / 1000)
        const timeUntilExpiry = tokenPayload.exp - currentTime
        
        // 如果token在5分钟内过期，则刷新
        if (timeUntilExpiry < 300) {
          const newToken = await apiRequest.refreshAccessToken(refreshToken)
          if (newToken) {
            // 刷新token后，获取最新的用户信息
            await this.refreshUserInfo()
            return true
          } else {
            return false
          }
        } else {
          // Token有效，获取用户信息
          await this.refreshUserInfo()
          return true
        }
      }
      
      return true
    } catch (error) {
      return false
    }
  },

  // 智能错误处理 - 根据错误类型决定是否清除token
  handleApiError(error: any): boolean {
    // 如果是网络错误或超时，不清除token
    if (error.code === 'ECONNABORTED' || 
        error.message.includes('timeout') || 
        error.message.includes('Network Error')) {
      return false
    }
    
    // 如果是服务器错误（5xx），不清除token
    if (error.response?.status >= 500) {
      return false
    }
    
    // 如果是403错误（权限不足），不清除token
    if (error.response?.status === 403) {
      return false
    }
    
    // 如果是404错误，不清除token
    if (error.response?.status === 404) {
      return false
    }
    
    // 如果是401错误，需要清除token
    if (error.response?.status === 401) {
      return true
    }
    return false
  },

  // 解析JWT token
  parseJwtToken(token: string): any {
    try {
      if (!token || typeof token !== 'string' || !token.includes('.')) {
        return null
      }

      const parts = token.split('.')
      if (parts.length !== 3) {
        return null
      }

      const base64Url = parts[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      
      // 添加padding以确保base64解码正确
      const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4)
      
      let decodedPayload: string
      try {
        decodedPayload = atob(paddedBase64)
      } catch (decodeError) {
        console.warn('Base64解码失败:', decodeError)
        return null
      }

      const jsonPayload = decodeURIComponent(decodedPayload.split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.warn('解析JWT token失败:', error)
      return null
    }
  },

  // 刷新用户信息（包括角色和权限）
  async refreshUserInfo(): Promise<JwtUserInfo | null> {
    try {
      // 使用 user store 的 refreshUserInfo 方法
      const userStore = useUserStore()
      const result = await userStore.refreshUserInfo()
      return result || null
    } catch (error) {
      console.error('❌ 刷新用户信息失败:', error)
      // 检查是否需要清除token
      if (this.handleApiError(error)) {
        authStateManager.clearAuthState()
      }
      return null
    }
  }
}

// 导出默认认证API
export default authApi 